/**
 * Authenticated API helper utility
 * Automatically includes Clerk JWT token in all API requests
 */

/**
 * Make an authenticated API request
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, body, etc.)
 * @param {function} getToken - Clerk's getToken function
 * @returns {Promise<Response>} - Fetch response
 */
export async function authenticatedFetch(url, options = {}, getToken) {
  try {
    // Get JWT token from Clerk
    const token = await getToken();
    
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    // Add Authorization header
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    
    // Make the request
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * Helper functions for common API operations
 */

export const api = {
  // Generic GET request
  get: async (url, getToken) => {
    const response = await authenticatedFetch(url, { method: 'GET' }, getToken);
    return response.json();
  },
  
  // Generic POST request
  post: async (url, data, getToken) => {
    const response = await authenticatedFetch(
      url,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      getToken
    );
    return response.json();
  },
  
  // Generic PUT request
  put: async (url, data, getToken) => {
    const response = await authenticatedFetch(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      getToken
    );
    return response.json();
  },
  
  // Generic DELETE request
  delete: async (url, getToken) => {
    const response = await authenticatedFetch(
      url,
      {
        method: 'DELETE',
      },
      getToken
    );
    return response.json();
  },
};

